import { h as __h } from "preact";
import { signal } from "@preact/signals";
/** @import { Signal } from "@preact/signals"; */
import { Component } from "preact";
/** @import Core from "~/app/lib/core/core.js"; */

import { tfaSetupResponse } from "~/backend/validate.js";
import { apiRequest, ApiError, response } from "~/lib/api.js";
import { logInWithPassword, logInWithPasskey } from "~/lib/login.js";
import { getPasskeyCredential } from "~/lib/passkey.js";
import { setSession } from "~/lib/session.js";
import html from "~/util/html.js";
import { persist } from "~/util/persist.js";
import { PromiseSignal } from "~/util/promise.js";
import { navigate } from "~/util/router.js";
import { object, parse, string } from "~/util/validate.js";
import Button from "~/component/Button.js";
import Field from "~/component/Field.js";
import Icon from "~/component/Icon.js";
import Modal from "~/component/Modal.js";
import { EnrollTotpModal } from "~/component/Totp.js";
import PublicLayout, { FooterPrompt } from "./PublicLayout.js";
import input from "~/style/input.js";
import link from "~/style/link.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Login.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Login.module.css", import.meta.url);
const css = adopt(sheet);

/** @extends {ApiError<"email" | "password" | "machine_id">} */
class LogInError extends ApiError {
  /** @override */
  get messages() {
    return {
      [ApiError.noField]: {
        invalid_credentials: "Your credentials are incorrect.",
        invalid_machine_id: "This computer is already associated with another user."
      },
      email: {
        "validation_failed.required": "Email address is required.",
        "validation_failed.email": "Email address is invalid."
      },
      password: {
        "validation_failed.required": "Password is required."
      },
      machine_id: {
        "validation_failed.sha256": "Phaze is unable to identify this computer. Please contact support.",
        "validation_failed.required": "Error finding machine ID."
      }
    };
  }
}
class PasskeyError extends ApiError {
  /** @override */
  get messages() {
    return {
      [ApiError.noField]: {
        unknown_error: "Couldn't accept passkey.",
        passkey_error: "Couldn't find passkey.",
        no_credential: "Couldn't find passkey."
      }
    };
  }
}

/** @extends {ApiError<"tfa_code">} */
class TfaLoginError extends ApiError {
  /** @override */
  get messages() {
    return {
      [ApiError.noField]: {
        invalid_credentials: "Invalid code. Please try again, or use one of your backup codes."
      },
      tfa_code: {
        "validation_failed.required": "Code is required.",
        "validation_failed.alphanum": "Code must only contain letters and numbers."
      }
    };
  }
}

/**
 * @extends {Component<Props>}
 *
 * @typedef {object} Props
 * @property {Core} [core]
 */
export default class LogIn extends Component {
  /** @type {Signal<{ email: string; password: string } | undefined>} */
  #tfaVerify = signal(undefined);

  /** @type {Signal<{ email: string; password: string; token: string; uri: string } | undefined>} */
  #tfaSetup = signal(undefined);
  promptForPasskey = signal(false);
  showReplaceMachineIdModal = signal(false);
  confirmInput = signal("");
  constructor() {
    super();
    persist(this.promptForPasskey, "prompt_for_passkey");
  }

  /** @override */
  componentDidMount() {
    if (this.promptForPasskey.value) this.#logInWithPasskey.invoke();
  }
  #logInWithEmail = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    const form = new FormData(ev.currentTarget);
    const data = parse(object({
      email: string,
      password: string
    }), Object.fromEntries(form.entries()));
    try {
      const session = await logInWithPassword(data.email, data.password, {
        machineId: this.props.core?.state.machine_id
      });
      setSession(session);
      this.promptForPasskey.value = false;
    } catch (err) {
      switch (ApiError.from(err).code) {
        case "tfa_required":
          this.#tfaVerify.value = {
            email: data.email,
            password: data.password
          };
          break;
        case "tfa_setup_required":
          {
            const res = await apiRequest("POST", "/v2/tfa/setup", {
              json: {
                email: data.email,
                password: data.password
              },
              schema: response(tfaSetupResponse)
            });
            this.#tfaSetup.value = {
              email: data.email,
              password: data.password,
              token: res.data.token,
              uri: res.data.tfa_auth_uri
            };
            break;
          }
        default:
          this.#checkForMachineIdError(err);
          throw err;
      }
    }
  });
  #logInWithPasskey = new PromiseSignal(async () => {
    try {
      const credential = await getPasskeyCredential();
      const session = await logInWithPasskey(credential, {
        machineId: this.props.core?.state.machine_id
      });
      setSession(session);
      this.promptForPasskey.value = true;
    } catch (e) {
      this.promptForPasskey.value = false;
      this.#checkForMachineIdError(e);
      throw e;
    }
  });
  #logInAfterTfaSetup = new PromiseSignal(async (/** @type {string | undefined} */tfa) => {
    if (this.#tfaSetup.value === undefined) return;
    const {
      email,
      password
    } = this.#tfaSetup.value;
    const session = await logInWithPassword(email, password, {
      tfa,
      machineId: this.props.core?.state.machine_id
    });
    setSession(session);
  });
  handleReplaceMachineId = () => {
    if (!this.props.core) return;
    this.props.core.replaceMachineId();
    this.showReplaceMachineIdModal.value = false;
    this.confirmInput.value = "";
  };
  handleCloseModal = () => {
    this.showReplaceMachineIdModal.value = false;
    this.confirmInput.value = "";
  };

  /** @param {unknown} e */
  #checkForMachineIdError(e) {
    const error = e instanceof Error ? LogInError.from(e) : null;
    if (error?.code === "invalid_machine_id") {
      this.showReplaceMachineIdModal.value = true;
    }
  }
  render() {
    if (this.#tfaVerify.value) {
      return __h(TfaVerify, {
        email: this.#tfaVerify.value.email,
        password: this.#tfaVerify.value.password,
        machineId: this.props.core?.state.machine_id,
        onVerify: () => {
          this.#tfaVerify.value = undefined;
        },
        onBack: () => {
          this.#tfaVerify.value = undefined;
        }
      });
    }
    const logInWithEmailError = this.#logInWithEmail.error && LogInError.from(this.#logInWithEmail.error);
    const logInAfterTfaSetupError = this.#logInAfterTfaSetup.error && LogInError.from(this.#logInAfterTfaSetup.error);
    const passkeyError = this.#logInWithPasskey.error && PasskeyError.from(this.#logInWithPasskey.error);
    const formContent = __h(PublicLayout, {
      title: "Log into Phaze",
      key: "login",
      onSubmit: (/** @type {SubmitEvent & { currentTarget: HTMLFormElement }} */ev) => {
        ev.preventDefault();
        this.#logInWithEmail.invoke(ev);
      }
    }, __h(Field, {
      "for": "email",
      label: "Email",
      error: logInWithEmailError?.fields.email?.message
    }, __h("input", {
      "class": input.input,
      id: "email",
      type: "email",
      name: "email",
      required: true
    })), __h(Field, {
      "for": "password",
      label: "Password",
      error: logInWithEmailError?.fields.password?.message,
      info: [" ", __h("a", {
        "class": css.forgot,
        href: "/forgot/"
      }, "Forgot password?"), " "]
    }, __h("input", {
      "class": input.input,
      id: "password",
      type: "password",
      name: "password",
      required: true
    })), logInWithEmailError?.fields.machine_id?.message && __h("p", {
      "class": css.error
    }, logInWithEmailError?.fields.machine_id?.message), logInWithEmailError && __h("p", {
      "class": css.error
    }, logInWithEmailError?.message), logInAfterTfaSetupError && __h("p", {
      "class": css.error
    }, logInAfterTfaSetupError?.message), passkeyError && __h("p", {
      "class": css.error
    }, passkeyError?.message), __h(Button, {
      level: "primary",
      type: "submit",
      loading: this.#logInWithEmail.loading
    }, "Log in"), __h(Button, {
      type: "button",
      loading: this.#logInWithPasskey.loading,
      onClick: this.#logInWithPasskey.invoke
    }, "Log in with a passkey"), __h(Button, {
      type: "button",
      onClick: () => {
        const emailInput = /** @type {HTMLInputElement | null} */
        document.getElementById("email");
        navigate("/login/sso/", {
          state: {
            email: emailInput?.value
          }
        });
      }
    }, "Sign in with your company"), __h(FooterPrompt, {}, " New to Phaze? ", __h("a", {
      href: "/signup/"
    }, "Create an account"), " "), this.props.core?.state.debug && __h("div", {
      "class": css.machine
    }, __h(Icon, {
      name: "potion"
    }), __h(FooterPrompt, {}, __h("a", {
      href: "/login/machine/"
    }, "Machine key login"))));
    return [formContent, __h(EnrollTotpModal, {
      uri: this.#tfaSetup.value?.uri,
      token: this.#tfaSetup.value?.token,
      description: "Your organization now requires two-factor authentication. Set up an authenticator app to continue logging in.",
      onClose: async (/** @type {string | undefined} */tfa) => {
        if (tfa && this.#tfaSetup.value) await this.#logInAfterTfaSetup.invoke(tfa);
        this.#tfaSetup.value = undefined;
      }
    }), __h(Modal, {
      title: "Computer already linked",
      open: this.showReplaceMachineIdModal.value,
      onClose: this.handleCloseModal
    }, __h("p", {}, "This computer is already registered with a different Phaze account."), __h("p", {}, "To log in with a new account, you'll need to reset this computer's registration. You'll be\n\t\t\t\t\tprompted for administrator access, and Phaze will restart."), __h(Field, {
      "for": "confirm-replace",
      label: "Type 'reset registration' to continue"
    }, __h("input", {
      "class": input.input,
      id: "confirm-replace",
      type: "text",
      placeholder: "reset registration",
      value: this.confirmInput.value,
      onInput: /** @param {Event & { target: HTMLInputElement }} e */e => {
        this.confirmInput.value = e.target.value;
      },
      autocomplete: "off"
    })), __h("form", {
      method: "dialog",
      "class": css.modalActions
    }, __h(Button, {
      level: "primary",
      type: "button",
      onClick: this.handleReplaceMachineId,
      disabled: this.confirmInput.value.toLowerCase() !== "reset registration"
    }, "Continue"), __h(Button, {
      type: "button",
      onClick: this.handleCloseModal
    }, "Cancel")))];
  }
}

/**
 * @extends {Component<TfaVerifyProps>}
 *
 * @typedef {object} TfaVerifyProps
 * @property {string} email
 * @property {string} password
 * @property {string} [machineId] Binds the session to this machine, so it can host
 * @property {() => void} onVerify
 * @property {() => void} onBack
 */
class TfaVerify extends Component {
  #verify = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    const form = new FormData(ev.currentTarget);
    const data = parse(object({
      tfa_code: string
    }), Object.fromEntries(form.entries()));
    const machineId = this.props.machineId;
    const session = await logInWithPassword(this.props.email, this.props.password, {
      machineId,
      tfa: data.tfa_code
    });
    setSession(session);
    this.props.onVerify();
  });
  render() {
    const error = this.#verify.error && TfaLoginError.from(this.#verify.error);
    return __h(PublicLayout, {
      title: "Log into Phaze",
      key: "tfa",
      onSubmit: (/** @type {SubmitEvent & { currentTarget: HTMLFormElement }} */ev) => {
        ev.preventDefault();
        this.#verify.invoke(ev);
      }
    }, __h("p", {}, "Enter a code from your authenticator app or a backup code."), __h(Field, {
      "for": "tfa_code",
      label: "Authenticator code",
      error: error?.fields.tfa_code?.message
    }, __h("input", {
      "class": input.input,
      id: "tfa_code",
      type: "text",
      name: "tfa_code",
      autocomplete: "one-time-code",
      required: true
    })), __h("p", {
      "class": css.error
    }, error?.message), __h(Button, {
      level: "primary",
      type: "submit",
      loading: this.#verify.loading
    }, " Verify "), __h(FooterPrompt, {}, __h("button", {
      type: "button",
      "class": link.link,
      onClick: this.props.onBack
    }, "Back to login")));
  }
}